import React, { useEffect } from 'react';
import { trackGiftDownload } from '../utils/supabase';

/**
 * GiftsStep Component
 * Final page displaying downloadable gifts
 */
const GiftsStep = ({ onGiftsClaimed, onBack }) => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Notify parent when gifts page is viewed
  useEffect(() => {
    if (onGiftsClaimed) {
      onGiftsClaimed();
    }
  }, [onGiftsClaimed]);

  // Track gift download
  const handleGiftClick = async (giftType) => {
    const submissionId = localStorage.getItem('submissionId');
    if (submissionId) {
      await trackGiftDownload(submissionId, giftType);
    }
  };

  const gifts = [
    {
      id: 1,
      title: 'Romantic Vibes',
      subtitle: 'Spotify Playlist',
      description: 'A curated collection of romantic songs to set the perfect mood for your game night',
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
        </svg>
      ),
      link: 'https://open.spotify.com/playlist/17tBcHYFiFN8gGKyQ6AUhd?si=b0d4e9729cfb44c9',
      isSpotify: true,
      spotifyEmbed: 'https://open.spotify.com/embed/playlist/17tBcHYFiFN8gGKyQ6AUhd?utm_source=generator',
      giftType: 'spotify_playlist'
    },
    {
      id: 2,
      title: '5 Ways to Play',
      subtitle: 'Game Guide',
      description: 'Creative variations and challenges to maximize fun with your couples tower game',
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
        </svg>
      ),
      link: 'https://drive.google.com/uc?export=download&id=1RsfiuW3Cp24JtG0PoF4WgbYfB3MEk1x0',
      giftType: 'game_guide'
    },
    {
      id: 3,
      title: 'Love & Connection',
      subtitle: 'Relationship eBook',
      description: 'A modern guide to deepening your connection and keeping the spark alive',
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
        </svg>
      ),
      link: 'https://drive.google.com/uc?export=download&id=1mASocAhcvUm3dkcmHJSKOG2GiT13x1Ae',
      giftType: 'relationship_ebook'
    }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="max-w-3xl w-full">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-light tracking-wide mb-4">
            Congratulations!
          </h2>
          <p className="text-xl text-gray-600">
            Here are your exclusive gifts:
          </p>
        </div>

        {/* Web App Banner */}
        <div className="mb-12 animate-fade-in" style={{ animationDelay: '150ms' }}>
          <div className="border-2 border-black bg-black text-white p-8 text-center">
            <div className="flex justify-center mb-4">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-light tracking-wide mb-3">
              Your Web App is Ready
            </h3>
            <p className="text-gray-300 mb-2 leading-relaxed">
              Access your exclusive couples game web app anytime, anywhere.
            </p>
            <p className="text-sm text-gray-400 mb-6">
              Make sure to bookmark or save this page so you don't lose access!
            </p>
            <a
              href="https://spiciestower.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-3 bg-white text-black text-sm font-light tracking-wide
                       hover:bg-gray-200 transition-all duration-300
                       focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-black"
            >
              Open the Web App
            </a>
          </div>
        </div>

        {/* Gifts Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {gifts.map((gift, index) => (
            <div
              key={gift.id}
              className="border border-gray-200 bg-white p-8 transition-all duration-300 hover:border-black hover:shadow-xl animate-fade-in"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Icon */}
              <div className="flex justify-center mb-6 text-black">
                {gift.icon}
              </div>

              {/* Subtitle (badge style) */}
              <div className="text-center mb-3">
                <span className="inline-block px-3 py-1 text-xs font-light tracking-wider uppercase border border-gray-300 text-gray-600">
                  {gift.subtitle}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-xl font-light tracking-wide text-center mb-4">
                {gift.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-gray-600 text-center mb-8 leading-relaxed">
                {gift.description}
              </p>

              {/* Spotify Embed or Download Button */}
              {gift.isSpotify ? (
                <div className="space-y-3">
                  {/* Spotify Embed */}
                  <div className="overflow-hidden" style={{ borderRadius: '12px' }}>
                    <iframe
                      style={{ borderRadius: '12px' }}
                      src={gift.spotifyEmbed}
                      width="100%"
                      height="152"
                      frameBorder="0"
                      allowFullScreen=""
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      title="Spotify Playlist"
                    />
                  </div>

                  {/* Open in Spotify Button */}
                  <a
                    href={gift.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleGiftClick(gift.giftType)}
                    className="block w-full px-6 py-3 bg-black text-white text-center text-sm font-light tracking-wide
                             hover:bg-gray-800 transition-all duration-300
                             focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                  >
                    Open in Spotify
                  </a>
                </div>
              ) : (
                <a
                  href={gift.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleGiftClick(gift.giftType)}
                  className="block w-full px-6 py-3 bg-black text-white text-center text-sm font-light tracking-wide
                           hover:bg-gray-800 transition-all duration-300
                           focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                >
                  Download Now
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Thank You Message */}
        <div className="text-center space-y-4 pt-8 border-t border-gray-200 animate-fade-in"
             style={{ animationDelay: '450ms' }}>
          <p className="text-lg text-gray-700 leading-relaxed">
            Thank you for being part of our community!
          </p>
          <p className="text-base text-gray-600 leading-relaxed">
            We hope you enjoy these gifts and your couples tower game.
          </p>
          <p className="text-sm text-gray-500 pt-4">
            Check your email for the download links as well.
          </p>
        </div>
      </div>

      {/* Back Button - Bottom */}
      <button
        onClick={onBack}
        className="fixed bottom-6 left-1/2 transform -translate-x-1/2
                   w-12 h-12 rounded-full bg-white border border-black
                   flex items-center justify-center
                   hover:bg-gray-100 transition-all duration-200
                   focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2
                   shadow-sm"
      >
        <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
    </div>
  );
};

export default GiftsStep;
